"use client";
import { publicErrorMessage } from "@ocr-monorepo/shared";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useParams } from "next/navigation";
import Link from "next/link";
import { useQueryStates } from "nuqs";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  Database,
  Loader2,
  Play,
  RotateCcw,
  Table2,
} from "lucide-react";

import { Button } from "@ocr-monorepo/ui/components/button";
import { ButtonGroup } from "@ocr-monorepo/ui/components/button-group";
import { ScrollArea } from "@ocr-monorepo/ui/components/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ocr-monorepo/ui/components/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@ocr-monorepo/ui/components/toggle-group";
import { DataPaginator } from "@/components/data-paginator";
import { SqlCodeEditor } from "@/components/sql/sql-code-editor";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { tableQueryParams } from "@/lib/searchParams";
import { useRecordingDataAdapterOptional } from "@/components/recording-demo/recording-data-adapter";
import { PdfToSqliteDemoDocument } from "@/components/pdf-to-sqlite-demo-document";
import {
  DEFAULT_PDF_TO_SQLITE_DEMO_QUERY,
  PDF_TO_SQLITE_DEMO_QUERIES,
  findPdfToSqliteDemoQueryBySql,
  type PdfToSqliteDemoQueryResponse,
  type PdfToSqliteDemoSchemaResponse,
} from "@/lib/pdf-to-sqlite-demo";
import { captureGrowthEvent } from "@/lib/analytics";

const MAX_QUERY_ROWS = 100;
const DEFAULT_SQL = "SELECT * FROM your_table LIMIT 100;";

type ResultMode = "table" | "json";

const PUBLIC_DEMO_LIMITATION =
  "The public demo only runs the curated read-only queries shown here. Create a free workspace to run custom SQL.";

async function fetchPublicDemoSchema() {
  const response = await fetch("/api/pdf-to-sqlite-demo");
  const body = (await response.json()) as
    | PdfToSqliteDemoSchemaResponse
    | { error?: string };
  if (!response.ok || !("tables" in body)) {
    throw new Error(
      "error" in body && body.error
        ? body.error
        : "The live demo schema could not be loaded.",
    );
  }
  return body;
}

async function runPublicDemoQuery(sql: string) {
  const query = findPdfToSqliteDemoQueryBySql(sql);
  if (!query) throw new Error(PUBLIC_DEMO_LIMITATION);

  const response = await fetch("/api/pdf-to-sqlite-demo", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ queryId: query.id }),
  });
  const body = (await response.json()) as
    | PdfToSqliteDemoQueryResponse
    | { error?: string };
  if (!response.ok || !("rows" in body)) {
    throw new Error(
      "error" in body && body.error
        ? body.error
        : "The live demo query could not be completed.",
    );
  }
  return body;
}

function formatCell(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function quoteIdentifier(identifier: string) {
  return `"${identifier.replace(/"/g, '""')}"`;
}

export default function ProjectQueryPage() {
  return <ProjectQueryPageContent />;
}

export function ProjectQueryPageContent({
  projectSlugOverride,
  publicDemo = false,
}: {
  projectSlugOverride?: string;
  publicDemo?: boolean;
} = {}) {
  const params = useParams<{ projectSlug: string }>();
  const projectSlug = projectSlugOverride ?? params.projectSlug;
  const trpc = useTRPC();
  const recordingAdapter = useRecordingDataAdapterOptional();
  const [sql, setSql] = useState(
    publicDemo
      ? DEFAULT_PDF_TO_SQLITE_DEMO_QUERY.sql
      : recordingAdapter
        ? `SELECT * FROM "${recordingAdapter.tables[0]?.name ?? "documents"}" LIMIT 100;`
        : DEFAULT_SQL,
  );
  const [mode, setMode] = useState<ResultMode>("table");
  const [copied, setCopied] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const demoLoadedTrackedRef = useRef(false);
  const [{ page, count }, setPagination] = useQueryStates(tableQueryParams);

  const publicDemoSchemaQuery = useQuery({
    queryKey: ["pdf-to-sqlite-demo", "schema"],
    queryFn: fetchPublicDemoSchema,
    enabled: publicDemo,
    staleTime: 5 * 60 * 1_000,
  });

  useEffect(() => {
    if (
      publicDemo &&
      publicDemoSchemaQuery.isSuccess &&
      !demoLoadedTrackedRef.current
    ) {
      demoLoadedTrackedRef.current = true;
      captureGrowthEvent("sqlite_demo_loaded", {
        database: publicDemoSchemaQuery.data.database,
        table_count: publicDemoSchemaQuery.data.tables.length,
        total_records: publicDemoSchemaQuery.data.totalRecords,
      });
    }
  }, [publicDemo, publicDemoSchemaQuery.data, publicDemoSchemaQuery.isSuccess]);

  const preparedQueries = useQuery({ ...trpc.demo.preparedQueries.queryOptions({ projectSlug }), enabled: Boolean(projectSlug?.startsWith("demo-")) && !publicDemo && !recordingAdapter });
  useEffect(() => {
    if (preparedQueries.data?.[0] && sql === DEFAULT_SQL) setSql(preparedQueries.data[0].sql);
  }, [preparedQueries.data, sql]);

  const productionTablesQuery = useQuery({
    ...trpc.sqlite.tables.list.queryOptions(projectSlug),
    enabled: Boolean(projectSlug) && !recordingAdapter && !publicDemo,
  });
  const tablesQuery = publicDemo
    ? {
        data: publicDemoSchemaQuery.data?.tables.map(({ id, name, slug }) => ({
          id,
          name,
          slug,
        })),
        isLoading: publicDemoSchemaQuery.isLoading,
      }
    : recordingAdapter
      ? { data: recordingAdapter.tables, isLoading: false }
      : productionTablesQuery;
  const productionTableSchemas = useQueries({
    queries: (tablesQuery.data ?? []).map((table) => ({
      ...trpc.sqlite.tables.schema.queryOptions({
        projectSlug,
        tableSlug: table.slug ?? table.name,
      }),
      enabled: Boolean(projectSlug) && !recordingAdapter && !publicDemo,
    })),
  });
  const tableSchemas = publicDemo
    ? (publicDemoSchemaQuery.data?.tables ?? []).map((table) => ({
        data: table.columns.map((column) => ({
          ...column,
          prompt: null,
        })),
        isLoading: false,
        isError: false,
      }))
    : recordingAdapter
      ? recordingAdapter.tables.map((table) => ({
          data: (recordingAdapter.getTableData(table.slug)?.columns ?? []).map(
            (name, index) => ({
              cid: index,
              name,
              type: "TEXT",
              notnull: 0,
              primaryKey: 0,
              prompt: null,
              default: null,
            }),
          ),
          isLoading: false,
          isError: false,
        }))
      : productionTableSchemas;
  const productionTableCounts = useQueries({
    queries: (tablesQuery.data ?? []).map((table) => ({
      ...trpc.sqlite.tables.values.queryOptions({
        projectSlug,
        tableSlug: table.slug ?? table.name,
        pageIndex: 0,
        pageSize: 1,
      }),
      enabled: Boolean(projectSlug) && !recordingAdapter && !publicDemo,
    })),
  });
  const tableCounts = publicDemo
    ? (publicDemoSchemaQuery.data?.tables ?? []).map((table) => ({
        data: { total: table.total },
        isLoading: false,
      }))
    : recordingAdapter
      ? recordingAdapter.tables.map((table) => ({
          data: {
            total: recordingAdapter.getTableData(table.slug)?.rows.length ?? 0,
          },
          isLoading: false,
        }))
      : productionTableCounts;
  const productionExecuteQuery = useMutation(
    trpc.sqlite.query.execute.mutationOptions(),
  );
  const publicDemoExecuteQuery = useMutation({
    mutationFn: ({ sql: querySql }: { sql: string }) =>
      runPublicDemoQuery(querySql),
  });
  const [recordingQuery, setRecordingQuery] = useState<{
    data?: {
      rows: Array<Record<string, unknown>>;
      columns: string[];
      durationMs: number;
      rowsRead: number;
    };
    isPending: boolean;
    error?: Error;
  }>({ isPending: false });
  const executeQuery: any = publicDemo
    ? publicDemoExecuteQuery
    : recordingAdapter
      ? {
          ...recordingQuery,
          isError: Boolean(recordingQuery.error),
          mutate: (
            input: { sql: string },
            callbacks: { onSuccess?: (result: any) => void },
          ) => {
            setRecordingQuery({ isPending: true });
            window.setTimeout(() => {
              const normalizedSql = input.sql
                .toLowerCase()
                .replaceAll(/[_-]/g, " ");
              const selectedSlug =
                recordingAdapter.tables.find((table) =>
                  normalizedSql.includes(
                    table.name.toLowerCase().replaceAll(/[_-]/g, " "),
                  ),
                )?.slug ??
                recordingAdapter.tables.find((table) =>
                  normalizedSql.includes(
                    table.slug.toLowerCase().replaceAll(/[_-]/g, " "),
                  ),
                )?.slug ??
                recordingAdapter.tables[0]?.slug;
              if (!selectedSlug) return;
              const table = recordingAdapter.getTableData(selectedSlug);
              const columns = table?.columns ?? [];
              const rows = (table?.rows ?? []).map((row) =>
                Object.fromEntries(
                  columns.map((column) => [column, row[column]]),
                ),
              );
              const result = {
                rows,
                columns,
                durationMs: 650,
                rowsRead: rows.length,
              };
              setRecordingQuery({ data: result, isPending: false });
              callbacks.onSuccess?.(result);
            }, 650);
          },
          reset: () => setRecordingQuery({ isPending: false }),
        }
      : productionExecuteQuery;

  const rows: Array<Record<string, unknown>> = executeQuery.data?.rows ?? [];
  const columns: string[] = executeQuery.data?.columns ?? [];
  const hasRun = Boolean(executeQuery.data);
  const hasResults = rows.length > 0;
  const hasDocumentRows =
    publicDemo &&
    rows.some((row: Record<string, unknown>) =>
      /^SINV-\d{7}$/.test(String(row.invoice_number ?? "")),
    );
  const selectedPublicDemoQuery = publicDemo
    ? findPdfToSqliteDemoQueryBySql(sql)
    : undefined;

  const selectPublicDemoQuery = (queryId: string) => {
    const query = PDF_TO_SQLITE_DEMO_QUERIES.find(
      (candidate) => candidate.id === queryId,
    );
    if (!query) return;
    executeQuery.reset();
    setSql(query.sql);
    setElapsedMs(0);
    void setPagination({ page: 1 });
  };

  const setTableQuery = (tableName: string) => {
    if (publicDemo) {
      const query = PDF_TO_SQLITE_DEMO_QUERIES.find(
        (candidate) => candidate.table === tableName,
      );
      if (query) selectPublicDemoQuery(query.id);
      return;
    }
    setSql(
      `SELECT * FROM ${quoteIdentifier(tableName)} LIMIT ${MAX_QUERY_ROWS};`,
    );
  };

  const setColumnQuery = (tableName: string, columnName: string) => {
    if (publicDemo) {
      setTableQuery(tableName);
      return;
    }
    setSql(
      `SELECT ${quoteIdentifier(columnName)}\nFROM ${quoteIdentifier(tableName)}\nLIMIT ${MAX_QUERY_ROWS};`,
    );
  };

  const resultColumns = useMemo(
    () => columns.map((column) => ({ accessorKey: column, header: column })),
    [columns],
  );
  const resultsTable = useReactTable({
    data: rows,
    columns: resultColumns,
    state: { pagination: { pageIndex: page - 1, pageSize: count } },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const stopTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => stopTimer, []);

  const runQuery = () => {
    if (executeQuery.isPending || !sql.trim()) return;
    const publicQuery = publicDemo
      ? findPdfToSqliteDemoQueryBySql(sql)
      : undefined;
    if (publicDemo) {
      captureGrowthEvent("sqlite_demo_query_started", {
        query_id: publicQuery?.id ?? "custom",
      });
    }
    stopTimer();
    executeQuery.reset();
    setElapsedMs(0);
    startedAtRef.current = window.performance.now();
    timerRef.current = window.setInterval(() => {
      setElapsedMs(
        Math.max(
          0,
          Math.round(window.performance.now() - startedAtRef.current),
        ),
      );
    }, 40);

    executeQuery.mutate(
      {
        projectSlug,
        sql,
        maxRows: MAX_QUERY_ROWS,
      },
      {
        onSuccess: (result: { durationMs: number }) => {
          if (publicDemo) {
            captureGrowthEvent("sqlite_demo_query_completed", {
              query_id: publicQuery?.id ?? "custom",
              duration_ms: result.durationMs,
            });
          }
          stopTimer();
          setElapsedMs(result.durationMs);
          void setPagination({ page: 1 });
        },
        onError: (error: unknown) => {
          if (publicDemo) {
            captureGrowthEvent("sqlite_demo_query_failed", {
              query_id: publicQuery?.id ?? "custom",
              error_message:
                publicErrorMessage("system_error"),
            });
          }
          stopTimer();
          setElapsedMs(
            Math.max(
              0,
              Math.round(window.performance.now() - startedAtRef.current),
            ),
          );
        },
      },
    );
  };

  const resetQuery = () => {
    stopTimer();
    executeQuery.reset();
    setSql(
      publicDemo
        ? DEFAULT_PDF_TO_SQLITE_DEMO_QUERY.sql
        : recordingAdapter
          ? 'SELECT * FROM "Shipping Invoices" LIMIT 100;'
          : DEFAULT_SQL,
    );
    setElapsedMs(0);
    setMode("table");
    void setPagination({ page: 1 });
  };

  const copyResults = async () => {
    if (!executeQuery.data) return;
    await navigator.clipboard.writeText(JSON.stringify(rows, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <header className="shrink-0 px-6 pb-5 pt-6 lg:px-8">
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">Query</h1>
        {publicDemo ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Live Database data · {publicDemoSchemaQuery.data?.totalRecords ?? 300}{" "}
            real records · curated read-only queries ·{" "}
            <Link
              href="/getting-started/create-account?source=pdf-to-sqlite-demo-query"
              onClick={() =>
                captureGrowthEvent("sqlite_demo_signup_clicked", {
                  source: "query_header",
                })
              }
              className="font-semibold text-primary hover:underline"
            >
              Create your document database

            </Link>
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">
            Explore project data with read-only SQL
          </p>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col border-t border-border">
        <div className="relative flex h-7 shrink-0 items-center gap-2 overflow-hidden border-b border-border px-4 font-mono text-[10px] text-muted-foreground">
          {executeQuery.isPending ? (
            <span className="relative flex size-2 items-center justify-center">
              <span className="absolute size-2 animate-ping rounded-full bg-primary/40 motion-reduce:animate-none" />
              <span className="relative size-1.5 rounded-full bg-primary" />
            </span>
          ) : (
            <span
              className={cn(
                "size-1.5 rounded-full",
                executeQuery.isError
                  ? "bg-destructive"
                  : hasRun
                    ? "bg-primary"
                    : "bg-muted-foreground",
              )}
            />
          )}
          {executeQuery.isPending
            ? "Query running"
            : executeQuery.isError
              ? "Query failed"
              : hasRun
                ? "Query completed successfully"
                : "Ready to run"}
          {executeQuery.isPending ? (
            <span className="tabular-nums">
              · {elapsedMs.toLocaleString()} ms
            </span>
          ) : null}
          <span className="ml-auto">
            {publicDemo ? "Curated SELECTs" : "Read only"} · {MAX_QUERY_ROWS}{" "}
            row limit
          </span>
          {executeQuery.isPending ? (
            <span
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 h-px origin-left animate-pulse bg-primary motion-reduce:animate-none"
            />
          ) : null}
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside
            className="hidden min-h-0 overflow-auto border-r border-border lg:block"
            aria-label="Database schema"
          >
            <div className="flex h-9 items-center gap-2 border-b border-border px-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Database className="size-3.5" /> Schema
            </div>
            <div className="py-1">
              {tablesQuery.isLoading ? (
                <p className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                  Loading schema…
                </p>
              ) : (
                (tablesQuery.data ?? []).map((table, index) => (
                  <details key={table.id} open={index === 0} className="group">
                    <summary className="flex h-8 cursor-pointer list-none items-center gap-1.5 px-3 text-xs hover:bg-muted/60">
                      <ChevronRight className="size-3 text-muted-foreground transition-transform group-open:rotate-90" />
                      <Table2 className="size-3.5 text-muted-foreground" />
                      <button
                        type="button"
                        className="min-w-0 flex-1 truncate text-left font-medium"
                        onClick={(event) => {
                          event.preventDefault();
                          setTableQuery(table.name);
                        }}
                      >
                        {table.name}
                      </button>
                      <span className="font-mono text-[9px] tabular-nums text-muted-foreground">
                        {tableCounts[index]?.isLoading
                          ? "…"
                          : (tableCounts[index]?.data?.total.toLocaleString() ??
                            "—")}
                      </span>
                    </summary>
                    <div className="border-l border-border pl-7">
                      {tableSchemas[index]?.isLoading ? (
                        <div className="flex h-7 items-center gap-1.5 px-2 font-mono text-[10px] text-muted-foreground">
                          <Loader2 className="size-3 animate-spin" />
                          Loading columns…
                        </div>
                      ) : tableSchemas[index]?.isError ? (
                        <p className="px-2 py-1.5 font-mono text-[10px] text-destructive">
                          Schema unavailable
                        </p>
                      ) : (
                        (tableSchemas[index]?.data ?? []).map((column) => (
                          <button
                            key={column.name}
                            type="button"
                            onClick={() =>
                              setColumnQuery(table.name, column.name)
                            }
                            className="block h-7 w-full truncate px-2 text-left font-mono text-[11px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                          >
                            {column.name}
                          </button>
                        ))
                      )}
                    </div>
                  </details>
                ))
              )}
            </div>
          </aside>

          <main className="grid min-h-0 min-w-0 grid-rows-[234px_minmax(0,1fr)]">
            <section
              className="min-h-0 border-b border-border"
              aria-labelledby="query-editor-heading"
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  event.preventDefault();
                  runQuery();
                }
              }}
            >
              <div className="flex h-9 items-center justify-between border-b border-border px-3">
                <h2
                  id="query-editor-heading"
                  className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                >
                  SQL editor
                </h2>
                <div className="flex items-center gap-3">
                  {publicDemo ? (
                    <Select
                      value={selectedPublicDemoQuery?.id}
                      onValueChange={selectPublicDemoQuery}
                    >
                      <SelectTrigger
                        size="sm"
                        className="h-7 w-48 text-[10px]"
                        aria-label="Choose a live demo query"
                      >
                        <SelectValue placeholder="Custom query (limited)" />
                      </SelectTrigger>
                      <SelectContent>
                        {PDF_TO_SQLITE_DEMO_QUERIES.map((query) => (
                          <SelectItem key={query.id} value={query.id}>
                            {query.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                  <span className="hidden font-mono text-[9px] text-muted-foreground sm:inline">
                    ⌘ Enter to run
                  </span>
                  <Button
                    size="sm"
                className="min-w-16"
                    onClick={runQuery}
                    disabled={executeQuery.isPending || !sql.trim()}
                  >
                    {executeQuery.isPending ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <Play className="size-3" />
                    )}
                    {executeQuery.isPending ? "Running" : "Run"}
                  </Button>
                </div>
              </div>
              {!!preparedQueries.data?.length && <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2"><span className="text-xs text-muted-foreground">Starter queries</span>{preparedQueries.data.map(query => <Button key={query.name} variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSql(query.sql)}>{query.name}</Button>)}</div>}
              <SqlCodeEditor
                value={sql}
                onChange={setSql}
                ariaLabel="SQL query editor"
                height="198px"
                className="border-0"
              />
            </section>

            <section className="flex min-h-0 flex-col overflow-hidden">
              <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border bg-background px-2 py-1.5">
                <ToggleGroup
                  type="single"
                  value={mode}
                  onValueChange={(value) =>
                    value && setMode(value as ResultMode)
                  }
                  variant="outline"
                  size="sm"
                  spacing={0}
                  aria-label="Result display"
                  className="h-7 bg-background"
                >
                  <ToggleGroupItem
                    value="table"
                    aria-label="Show table results"
                    className="h-7 px-3 text-[10px] data-[state=on]:bg-foreground data-[state=on]:text-background"
                  >
                    Table
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="json"
                    aria-label="Show JSON results"
                    className="h-7 px-3 text-[10px] data-[state=on]:bg-foreground data-[state=on]:text-background"
                  >
                    JSON
                  </ToggleGroupItem>
                </ToggleGroup>
                <ButtonGroup className="h-7 bg-background">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyResults}
                    disabled={!hasRun || executeQuery.isPending}
                  >
                    {copied ? (
                      <Check className="size-3" />
                    ) : (
                      <Copy className="size-3" />
                    )}
                    {copied ? "Copied" : "Copy results"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetQuery}
                    disabled={executeQuery.isPending}
                  >
                    <RotateCcw className="size-3" /> Reset
                  </Button>
                </ButtonGroup>
              </div>

              <ScrollArea
                className="min-h-0 flex-1"
                scrollbarOrientation="both"
              >
                {executeQuery.isPending ? (
                  <div className="flex h-full min-h-56 items-center justify-center p-8 text-center">
                    <div className="min-w-52">
                      <Loader2 className="mx-auto size-5 animate-spin text-muted-foreground" />
                      <p className="mt-3 text-sm font-semibold">
                        Running query
                      </p>
                      <p className="mt-1 font-mono text-[10px] tabular-nums text-muted-foreground">
                        Waiting for database · {elapsedMs.toLocaleString()} ms
                      </p>
                      <div className="mx-auto mt-3 h-1 w-40 overflow-hidden rounded-full bg-muted">
                        <div className="h-full w-2/3 animate-pulse bg-primary motion-reduce:animate-none" />
                      </div>
                    </div>
                  </div>
                ) : executeQuery.isError ? (
                  <div className="flex h-full min-h-56 items-center justify-center p-8 text-center">
                    <div className="max-w-md">
                      <AlertTriangle className="mx-auto size-5 text-destructive" />
                      <p className="mt-3 text-sm font-semibold">Query failed</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {publicErrorMessage("system_error")}
                      </p>
                      {publicDemo ? (
                        <Link
                          href="/getting-started/create-account?source=pdf-to-sqlite-demo-limit"
                          onClick={() =>
                            captureGrowthEvent("sqlite_demo_signup_clicked", {
                              source: "query_error",
                            })
                          }
                          className="mt-3 inline-flex text-xs font-semibold text-primary hover:underline"
                        >
                          Create a free workspace for custom SQL →
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ) : !hasRun ? (
                  <div className="flex h-full min-h-56 items-center justify-center p-8 text-center">
                    <div>
                      <Database className="mx-auto size-5 text-muted-foreground" />
                      <p className="mt-2 text-sm font-semibold">
                        Ready to query
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {publicDemo
                          ? "Choose one of the live, curated queries above."
                          : "Choose a table or write a read-only SQL query."}
                      </p>
                    </div>
                  </div>
                ) : mode === "json" ? (
                  <pre className="min-h-full animate-in p-4 font-mono text-[11px] leading-5 text-foreground fade-in duration-300">
                    {JSON.stringify(rows, null, 2)}
                  </pre>
                ) : hasResults ? (
                  <table className="w-full min-w-max animate-in border-collapse text-xs fade-in duration-300">
                    <thead className="sticky top-0 z-10 bg-sidebar">
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column}
                            className="h-8 border-b border-border px-3 text-center font-mono text-[10px] font-medium tracking-[0.02em] text-muted-foreground"
                          >
                            {column}
                          </th>
                        ))}
                        {hasDocumentRows ? (
                          <th className="h-8 border-b border-border px-3 text-center font-mono text-[10px] font-medium tracking-[0.02em] text-muted-foreground">
                            source PDF
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {resultsTable
                        .getRowModel()
                        .rows.map(({ id, original }) => (
                          <tr key={id} className="h-9 hover:bg-muted/30">
                            {columns.map((column) => (
                              <td
                                key={column}
                                className="max-w-[320px] border-b border-border px-3 text-center text-xs"
                              >
                                <span className="block truncate">
                                  {formatCell(original[column])}
                                </span>
                              </td>
                            ))}
                            {hasDocumentRows ? (
                              <td className="border-b border-border px-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedDocument(
                                      String(original.invoice_number),
                                    )
                                  }
                                  className="whitespace-nowrap font-mono text-[10px] font-semibold text-primary hover:underline"
                                >
                                  View document
                                </button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex h-full min-h-56 items-center justify-center p-8 text-center">
                    <p className="text-xs text-muted-foreground">
                      Query completed with no rows.
                    </p>
                  </div>
                )}
              </ScrollArea>

              <footer className="flex min-h-14 shrink-0 flex-wrap items-center gap-x-5 border-t border-border px-3">
                <dl className="flex shrink-0 items-center gap-5 py-2">
                  <div className="flex items-baseline gap-1.5">
                    <dt className="text-[10px] text-muted-foreground">Rows</dt>
                    <dd className="font-mono text-[10px] font-semibold tabular-nums">
                      {hasRun ? rows.length.toLocaleString() : "—"}
                    </dd>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <dt className="text-[10px] text-muted-foreground">Time</dt>
                    <dd className="font-mono text-[10px] font-semibold tabular-nums">
                      {executeQuery.isPending
                        ? `${elapsedMs.toLocaleString()} ms`
                        : hasRun
                          ? `${executeQuery.data?.durationMs.toLocaleString()} ms`
                          : "—"}
                    </dd>
                  </div>
                  <div className="hidden items-baseline gap-1.5 sm:flex">
                    <dt className="text-[10px] text-muted-foreground">
                      Scanned
                    </dt>
                    <dd className="font-mono text-[10px] font-semibold tabular-nums">
                      {hasRun
                        ? executeQuery.data?.rowsRead.toLocaleString()
                        : "—"}
                    </dd>
                  </div>
                </dl>
                {mode === "table" && hasResults ? (
                  <DataPaginator
                    table={resultsTable}
                    totalItems={rows.length}
                    showInfo
                    showPageSizeSelector
                    pageSizeOptions={[10, 20, 50, 100]}
                    className="ml-auto min-h-0 flex-1 justify-end gap-x-3 py-1.5"
                  />
                ) : null}
              </footer>
            </section>
          </main>
        </div>
      </div>
      {publicDemo ? (
        <PdfToSqliteDemoDocument
          invoiceNumber={selectedDocument}
          onOpenChange={(open) => {
            if (!open) setSelectedDocument(null);
          }}
        />
      ) : null}
    </div>
  );
}
